<template>
  <v-card>
    <v-card-title class="primary">
      <span class="headline white--text">{{title}}</span>
      <v-spacer/>
      <Icon
      icon="mdi-close"
      iconColor="white"
      dataTestId="address-close-button"
      :small="false"
      :large="true"
      :tooltipText="$t('close')"
      @icon-clicked="onClose"
      />
    </v-card-title>
    <v-card-text>
      <FormWrapper :buttonText="submitBtnText" :disabled="isValidating" @submit="submitAddress" strongValidation>
        <v-tabs v-model="activeTab" class="pt-4">
          <v-tab>
            <Icon
            class="mr-1"
            :iconColor="activeTab === 0 ? 'primary' : 'grey'"
            icon="mdi-home-map-marker"
            />
            <v-spacer/>
            {{  $t('streetAddressOne') }}
          </v-tab>
          <v-tab>
            <Icon
            class="mr-1"
            :iconColor="activeTab === 1 ? 'primary' : 'grey'"
            icon="mdi-mailbox"
            />
            <v-spacer/>
            {{  $t('poBox') }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab" touchless>
          <v-tab-item>
            <v-col cols="12">
              <v-text-field
              v-model="address.street1"
              ref="firstField"
              maxLength="64"
              counter="64"
              color="black"
              data-testid="address-street1"
              :label="$t('streetAddressOne')"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
              v-model="address.street2"
              maxLength="64"
              counter="64"
              color="black"
              data-testid="address-street2"
              :label="$t('streetAddressTwo')"
              />
            </v-col>
          </v-tab-item>
          <v-tab-item>
            <v-col cols="12">
              <v-text-field
              v-model="poBox"
              ref="firstField"
              maxLength="64"
              counter="64"
              color="black"
              data-testid="address-street1"
              :label="$t('poBoxNumber')"
              :rules="[rules.numberOrBlank]"
              />
            </v-col>
          </v-tab-item>
        </v-tabs-items>
        <v-container fluid grid-list-md>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
              v-model="address.city"
              maxLength="32"
              counter="32"
              color="black"
              data-testid="address-city"
              :label="$t('city')"
              :rules="[rules.validCity]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-autocomplete
              v-model="address.state"
              color="black"
              item-color="primary"
              data-testid="address-state"
              :items="states"
              :label="$t('state')"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
              v-model="address.zip"
              color="black"
              maxlength="5"
              counter
              data-testid="address-zip"
              :label="$t('zipCode')"
              :rules="[rules.number]"
              />
            </v-col>
          </v-row>
          <v-row v-if="isValidating">
            <v-col cols="12" class="text-center">
              <v-progress-circular
              indeterminate
              color="primary"
              />
            </v-col>
          </v-row>
          <v-row v-else-if="showAddressSuggestions">
            <v-col cols="12">
              <v-card outlined>
                <div style="max-height: max(256px, calc(90vh - 580px)); overflow-y: auto;">
                  <v-card-title>
                    {{ $t('selectAddress') }}
                  </v-card-title>
                  <div class="px-6 pb-2">
                    <v-radio-group v-model="selectedValidationAddressIndex" class="mt-0">
                      <v-radio
                      v-for="(addr, index) in suggestedAddresses"
                      :key="index"
                      :value="index"
                      :label="addr.formattedAddressString"
                      color="primary"
                      />
                      <template v-if="showKeepAsTyped">
                        <v-divider v-if="hasSuggestedAddresses" class="mb-4"/>
                        <div class="caption text-uppercase grey--text text--darken-1 mb-2">
                          {{ $t('unverified') }}
                        </div>
                        <v-radio
                        :value="KeepAsTyped"
                        color="primary"
                        data-testid="address-keep-as-typed"
                        :label="typedAddressString"
                        />
                      </template>
                    </v-radio-group>
                  </div>
                </div>
                <div v-if="keepAsTypedSelected" class="d-flex align-center orange lighten-5 px-6 py-3">
                  <v-icon color="warning" class="mr-3">mdi-alert-outline</v-icon>
                  <div>
                    <div class="body-2">{{ $t('addressSavedWithoutVerification') }}</div>
                    <div class="caption grey--text text--darken-1">
                      {{ $t('useOnlyWhenNotListed') }}
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
        <template v-slot:right-action>
          <v-checkbox
          v-if="!propAddress && type === 0"
          v-model="addBillingAddress"
          class="mr-2"
          color="primary"
          data-testid="address-billing-check"
          :label="$t('addBillingAddress')"/>
        </template>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { states } from '@/utils/constants.js'
import { mapActions } from 'vuex'
import { cloneDeep, uniqWith } from 'lodash'
import fieldRules from '@/utils/rules.js'

const Tab = {
  StreetAddress: 0,
  POBox: 1
}

const KeepAsTyped = -2

export default {
  name: 'AddressForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  data: () => ({
    address: {
      street1: '',
      street2: '',
      city: '',
      zip: '',
      state: ''
    },
    poBox: '',
    addBillingAddress: false,
    states,
    isValidating: false,
    suggestedAddresses: [],
    noSuggestionsFound: false,
    selectedValidationAddressIndex: null,
    validationErrors: null,
    rules: fieldRules.rules,
    activeTab: Tab.StreetAddress,
    KeepAsTyped
  }),

  watch: {
    activeTab () {
      this.resetValidation()
    },

    address: {
      deep: true,
      handler () {
        this.resetValidation()
      }
    },

    poBox () {
      this.resetValidation()
    }
  },

  props: {
    propAddress: {
      type: Object,
      required: false,
      default: undefined
    },
    type: {
      type: Number,
      required: false,
      default: undefined
    }
  },

  computed: {
    isEditing () {
      return this.propAddress !== undefined
    },

    newAccount () {
      return this.accountId === -1
    },

    hasSuggestedAddresses () {
      return this.suggestedAddresses.length > 0
    },

    showAddressSuggestions () {
      return this.hasSuggestedAddresses || this.noSuggestionsFound
    },

    keepAsTypedSelected () {
      return this.selectedValidationAddressIndex === KeepAsTyped
    },

    showKeepAsTyped () {
      const typed = this.normalize(this.typedAddressString)
      return !this.suggestedAddresses.some(addr => this.normalize(addr.formattedAddressString) === typed)
    },

    typedAddressString () {
      if (this.activeTab === Tab.POBox) {
        return `P.O. Box ${this.poBox}, ${this.address.city}, ${this.address.state} ${this.address.zip}`
      }

      const street2 = this.address.street2 ? `${this.address.street2}, ` : ''
      return `${this.address.street1}, ${street2}${this.address.city}, ${this.address.state} ${this.address.zip}`
    },

    submitBtnText () {
      if (!this.showAddressSuggestions) {
        return this.$t('validateAddress')
      }
      return this.isEditing ? this.$t('saveChanges') : this.$t('addAddress')
    },

    title () {
      return this.type === undefined
        ? this.isEditing ? this.$t('editingAddress') : this.$t('creatingAddress')
        : this.isEditing ? this.$t('editingAddress') : this.$t('creatingAddressOfType', { type: this.type === 0 ? 'Shipping' : 'Billing' })
    }
  },

  created () {
    if (this.propAddress) {
      this.address = {
        street1: this.propAddress.address.addressLine1,
        street2: this.propAddress.address.addressLine2,
        city: this.propAddress.address.city,
        zip: this.propAddress.address.postalCode,
        state: this.propAddress.address.state
      }
    }
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.firstField.focus()
    }, 0)
  },

  methods: {
    ...mapActions('account', ['validateAddress']),
    submitAddress () {
      if (this.showAddressSuggestions) {
        this.selectValidatedAddress()
        return
      }

      const emptyStreetOrPOBox = this.activeTab === Tab.StreetAddress ? !this.address.street1 : !this.poBox

      const incompleteAddress = !this.address.city || !this.address.zip || !this.address.state

      if (emptyStreetOrPOBox || incompleteAddress) {
        this.setSnackError(this.$t('fillAddress'))
      } else {
        this.addressValidation()
      }
    },

    keepAddressAsTyped () {
      this.resetValidation()
      this.addressValidated()
    },

    buildAddressQuery () {
      const line1 = this.activeTab === Tab.POBox ? `P.O. Box ${this.poBox}` : this.address.street1.trim()
      return `${line1}, ${this.address.city.trim()}, ${this.address.state} ${this.address.zip} US`
    },

    async addressValidation () {
      const requestedTab = this.activeTab
      const requestedQuery = this.buildAddressQuery()

      try {
        this.isValidating = true
        this.noSuggestionsFound = false

        const response = await this.validateAddress(requestedQuery)

        if (this.activeTab !== requestedTab || this.buildAddressQuery() !== requestedQuery) {
          return
        }

        if (!response || response.results.length === 0) {
          this.noSuggestionsFound = true
          this.selectedValidationAddressIndex = -1
          this.setSnackError(this.$t('addressValidationFailed'))
          return
        }

        const processedAddresses = []
        response.results.forEach(addr => {
          if (this.activeTab === Tab.POBox) {
            if (addr.type === 'Geography') {
              addr.address.municipality = addr.address.postalName
              processedAddresses.push(addr.address)
            }
          } else if (addr.address.postalCode && addr.address.postalCode.includes(',')) {
            const postalCodes = addr.address.postalCode.split(',').map(pc => pc.trim())
            postalCodes.forEach(postalCode => {
              const newAddr = cloneDeep(addr)
              newAddr.address.postalCode = postalCode
              newAddr.address.freeformAddress = addr.address.freeformAddress.replace(addr.address.postalCode, postalCode)
              processedAddresses.push(newAddr.address)
            })
          } else {
            processedAddresses.push(addr.address)
          }
        })

        const resolvedAddresses = this.activeTab === Tab.POBox
          ? processedAddresses
          : processedAddresses.filter(this.isFullyResolved)

        const exactMatch = this.findExactMatch(resolvedAddresses)

        if (exactMatch) {
          this.updateAddressWithValidated(exactMatch)
          this.addressValidated()
        } else {
          this.suggestedAddresses = resolvedAddresses.map(addr => {
            addr.formattedAddressString = this.formatSuggestedAddress(addr)
            return addr
          })
          this.suggestedAddresses = uniqWith(this.suggestedAddresses, this.compareFormattedString)
          this.selectedValidationAddressIndex = -1
          this.noSuggestionsFound = this.suggestedAddresses.length === 0
          this.showValidationDialog = true
        }
      } catch (error) {
        console.error(error)
        this.setSnackError(this.$t('addressValidationError'))
      } finally {
        this.isValidating = false
      }
    },

    isFullyResolved (address) {
      return !!(address.streetNumber && address.streetName && address.municipality && address.postalCode)
    },

    findExactMatch (addresses) {
      return addresses.find(addr => {
        const normalizedStreet = addr.streetNumber + ' ' + addr.streetName
        return (
          this.normalize(normalizedStreet) === this.normalize(this.address.street1) &&
          this.normalize(addr.municipality) === this.normalize(this.address.city) &&
          this.normalize(addr.countrySubdivision) === this.normalize(this.address.state) &&
          addr.postalCode === this.address.zip
        )
      })
    },

    normalize (str) {
      return str ? str.toLowerCase().replace(/\s+/g, ' ').trim() : ''
    },

    updateAddressWithValidated (validatedAddress) {
      if (this.activeTab === Tab.POBox) {
        this.address = {
          street1: this.address.street1,
          street2: '',
          city: validatedAddress.municipality,
          state: validatedAddress.countrySubdivision,
          zip: validatedAddress.postalCode
        }
        return
      }

      this.address = {
        street1: `${validatedAddress.streetNumber} ${validatedAddress.streetName}`,
        street2: this.address.street2,
        city: validatedAddress.municipality,
        state: validatedAddress.countrySubdivision,
        zip: validatedAddress.postalCode
      }
    },

    selectValidatedAddress () {
      if (this.selectedValidationAddressIndex === null) {
        return
      }

      if (this.selectedValidationAddressIndex === KeepAsTyped) {
        this.keepAddressAsTyped()
        return
      }

      if (this.selectedValidationAddressIndex < 0) {
        this.setSnackError(this.$t('selectAddress'))
        return
      }
      const selectedAddress = this.suggestedAddresses[this.selectedValidationAddressIndex]
      this.updateAddressWithValidated(selectedAddress)
      this.showValidationDialog = false
      this.addressValidated()
    },

    formatSuggestedAddress (address) {
      const street2 = this.address.street2 ? this.address.street2 + ', ' : ''

      if (this.activeTab === Tab.POBox) {
        return `P.O. Box ${this.poBox}, ${address.freeformAddress}`
      }
      return `${address.streetNumber} ${address.streetName}, ${street2}${address.municipality}, ${address.countrySubdivision} ${address.postalCode}`
    },

    addressValidated () {
      let addressLine1
      let addressLine2

      if (this.activeTab === Tab.POBox) {
        addressLine1 = `P.O. Box ${this.poBox}`
        addressLine2 = ''
      } else {
        addressLine1 = this.address.street1
        addressLine2 = this.address.street2
      }

      const editedAddress = {
        address: {
          type: this.type,
          addressLine1: addressLine1,
          addressLine2: addressLine2,
          city: this.address.city,
          postalCode: this.address.zip,
          state: this.address.state
        }
      }

      if (this.isEditing) {
        editedAddress.accountAddressId = this.propAddress.accountAddressId || undefined
        this.$emit('address-updated', editedAddress)
      } else {
        const addresses = []
        addresses.push(editedAddress)
        if (this.addBillingAddress) {
          const billingAddress = cloneDeep(editedAddress)
          billingAddress.address.type = 1
          addresses.push(billingAddress)
        }
        this.$emit('address-created', addresses)
      }
    },

    compareFormattedString (firstAddress, secondAddress) {
      return firstAddress.formattedAddressString === secondAddress.formattedAddressString
    },

    resetValidation () {
      this.suggestedAddresses = []
      this.noSuggestionsFound = false
      this.selectedValidationAddressIndex = null
    },

    onClose () {
      this.resetValidation()
      this.$emit('close')
    }
  }
}
</script>
